import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { Badge } from './ui/badge';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
const cityCoords: {
  name: string;
  lat: number;
  lng: number;
}[] = [{
  name: 'Phoenix',
  lat: 33.4484,
  lng: -112.074
}, {
  name: 'Scottsdale',
  lat: 33.4942,
  lng: -111.9261
}, {
  name: 'Tempe',
  lat: 33.4255,
  lng: -111.94
}, {
  name: 'Mesa',
  lat: 33.4152,
  lng: -111.8315
}, {
  name: 'Chandler',
  lat: 33.3062,
  lng: -111.8413
}, {
  name: 'Gilbert',
  lat: 33.3528,
  lng: -111.789
}, {
  name: 'Glendale',
  lat: 33.5387,
  lng: -112.186
}, {
  name: 'Peoria',
  lat: 33.5806,
  lng: -112.2374
}, {
  name: 'Surprise',
  lat: 33.6292,
  lng: -112.368
}, {
  name: 'Avondale',
  lat: 33.4356,
  lng: -112.3496
}, {
  name: 'Goodyear',
  lat: 33.4353,
  lng: -112.3577
}, {
  name: 'Queen Creek',
  lat: 33.2487,
  lng: -111.6343
}, {
  name: 'Cave Creek',
  lat: 33.8331,
  lng: -111.9507
}, {
  name: 'Paradise Valley',
  lat: 33.531,
  lng: -111.9426
}, {
  name: 'Fountain Hills',
  lat: 33.6117,
  lng: -111.7174
}, {
  name: 'Anthem',
  lat: 33.8672,
  lng: -112.1476
}];
function ServiceAreaMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [33.5, -111.95],
      zoom: 10,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
      doubleClickZoom: false
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19
    }).addTo(map);
    // Custom green glowing marker
    const createMarkerIcon = (isMain: boolean) => {
      const size = isMain ? 18 : 10;
      const glow = isMain ? 20 : 10;
      return L.divIcon({
        className: '',
        html: `<div style="
          width: ${size}px;
          height: ${size}px;
          background: #A3E635;
          border-radius: 50%;
          box-shadow: 0 0 ${glow}px rgba(163,230,53,0.8), 0 0 ${glow * 2}px rgba(163,230,53,0.4);
          border: 2px solid rgba(163,230,53,0.9);
        "></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
    };
    cityCoords.forEach(city => {
      const isPhoenix = city.name === 'Phoenix';
      const marker = L.marker([city.lat, city.lng], {
        icon: createMarkerIcon(isPhoenix)
      }).addTo(map);
      marker.bindTooltip(city.name, {
        permanent: false,
        direction: 'top',
        offset: [0, -10],
        className: 'leaflet-tooltip-custom'
      });
    });
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <div ref={mapRef} className="w-full h-full rounded-2xl" data-id="element-183" />;
}
export function ServiceArea() {
  const cities = ['Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Gilbert', 'Glendale', 'Peoria', 'Surprise', 'Avondale', 'Goodyear', 'Queen Creek', 'Cave Creek', 'Paradise Valley', 'Fountain Hills', 'Anthem'];
  return <section id="service-area" className="relative py-20 md:py-28 bg-navy" data-id="element-184">
      <style data-id="element-185">{`
        .leaflet-tooltip-custom {
          background: #0F172A !important;
          color: #A3E635 !important;
          border: 1px solid rgba(163,230,53,0.4) !important;
          font-family: 'Bebas Neue', sans-serif;
          font-size: 14px;
          letter-spacing: 0.05em;
          padding: 4px 10px !important;
          border-radius: 6px !important;
          box-shadow: 0 0 10px rgba(163,230,53,0.2) !important;
        }
        .leaflet-tooltip-custom::before {
          border-top-color: rgba(163,230,53,0.4) !important;
        }
        .leaflet-container {
          background: #0F172A !important;
        }
      `}</style>
      <div className="container mx-auto px-4" data-id="element-186">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12 md:mb-16" data-id="element-187">
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white tracking-wider mb-4" data-id="element-188">
            WE HAUL ACROSS <span className="text-lime" data-id="element-189">THE VALLEY</span>
          </h2>
          <div className="w-32 h-1 bg-lime mx-auto mb-6" data-id="element-190" />
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto" data-id="element-191">
            Based in Phoenix, serving every corner of the Valley of the Sun.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-192">
          {/* Left: Real Map */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-193">
            <div className="relative aspect-square max-w-md mx-auto overflow-hidden rounded-2xl border-2 border-lime/30 shadow-[0_0_40px_rgba(163,230,53,0.2)]" data-id="element-194">
              <ServiceAreaMap data-id="element-195" />
            </div>
          </motion.div>

          {/* Right: City Pills */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-8" data-id="element-196">
            <div className="flex flex-wrap gap-3" data-id="element-197">
              {cities.map((city, index) => <motion.div key={city} initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: index * 0.05
            }} data-id="element-198">
                  <Badge variant="outline" className="border-2 border-lime/40 text-white hover:bg-lime/10 hover:border-lime transition-all px-4 py-2 text-base font-medium cursor-default" data-id="element-199">
                    {city}
                  </Badge>
                </motion.div>)}
            </div>

            <p className="text-white/60 text-sm md:text-base" data-id="element-200">
              Not sure if you're in our zone? Text us your ZIP. We probably
              cover it.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Diagonal divider */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-navy clip-diagonal" data-id="element-201" />
    </section>;
}